/**
 * Mensajes de validación en español, escritos para que los lea una persona.
 *
 * Zod trae su propia traducción (`z.locales.es()`), pero suena a manual de
 * referencia: «Demasiado pequeño: se esperaba que texto tuviera >=8
 * caracteres». Este mapa se instala como `customError`, que tiene prioridad
 * sobre la traducción pero **no** sobre los mensajes que un esquema declare a
 * mano; devolver `undefined` deja que zod siga con su locale.
 */
import type { z } from 'zod';

type RawIssue = z.core.$ZodRawIssue;

/** Lee una propiedad opcional del problema sin romper el tipado del union. */
function read<T>(issue: RawIssue, key: string): T | undefined {
  return (issue as unknown as Record<string, unknown>)[key] as T | undefined;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

export function spanishMessage(issue: RawIssue): string | undefined {
  const origin = read<string>(issue, 'origin');

  switch (issue.code) {
    case 'invalid_type': {
      // `undefined` en un campo obligatorio: el usuario no ha escrito nada.
      if (issue.input === undefined || issue.input === null) return 'Este campo es obligatorio.';
      return 'El valor no tiene el formato esperado.';
    }

    case 'too_small': {
      const minimum = Number(read<unknown>(issue, 'minimum'));
      if (!Number.isFinite(minimum)) return undefined;
      if (origin === 'string') {
        if (minimum <= 1) return 'Este campo es obligatorio.';
        return `Escribe al menos ${minimum} ${plural(minimum, 'carácter', 'caracteres')}.`;
      }
      if (origin === 'array') {
        return `Selecciona al menos ${minimum} ${plural(minimum, 'elemento', 'elementos')}.`;
      }
      return `Debe ser ${minimum} como mínimo.`;
    }

    case 'too_big': {
      const maximum = Number(read<unknown>(issue, 'maximum'));
      if (!Number.isFinite(maximum)) return undefined;
      if (origin === 'string') {
        return `Como mucho ${maximum} ${plural(maximum, 'carácter', 'caracteres')}.`;
      }
      if (origin === 'array') {
        return `Como mucho ${maximum} ${plural(maximum, 'elemento', 'elementos')}.`;
      }
      return `Debe ser ${maximum} como máximo.`;
    }

    case 'invalid_format': {
      const format = read<string>(issue, 'format');
      if (format === 'email') return 'Introduce un correo electrónico válido.';
      if (format === 'date') return 'Introduce una fecha válida.';
      if (format === 'datetime') return 'Introduce una fecha y hora válidas.';
      if (format === 'uuid') return 'Identificador no válido.';
      // Los `regex` suelen traer su propio mensaje en el esquema.
      return undefined;
    }

    case 'invalid_value':
    case 'invalid_union':
      return 'Selecciona una de las opciones.';

    case 'not_multiple_of': {
      const step = Number(read<unknown>(issue, 'divisor'));
      return Number.isFinite(step) ? `Debe ser múltiplo de ${step}.` : undefined;
    }

    default:
      return undefined;
  }
}
